import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { resolveImage, categoryLabel, formatDate } from "@/lib/api";
import { AshokaChakra } from "@/components/AshokaChakra";

export function Hero({ featured }) {
  return (
    <section className="relative z-10 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 -z-10 tricolor-light-hero" />
      <div className="absolute inset-x-0 bottom-0 -z-10 h-32 bg-gradient-to-b from-transparent to-white/60" />
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 pt-14 sm:px-8 lg:grid-cols-12 lg:pb-24 lg:pt-20">
        <div className="lg:col-span-7">
          <p className="eyebrow reveal text-[var(--saffron-deep)]" style={{ animationDelay: "0.05s" }}>
            राष्ट्र प्रथम · Nation First · Unity in Diversity
          </p>
          <h1 className="reveal mt-5 font-serif text-4xl font-bold leading-[1.05] tracking-tight text-[var(--chakra-deep)] sm:text-5xl lg:text-6xl" style={{ animationDelay: "0.15s" }}>
            Stories of Bharat,<br />
            <span className="text-[var(--saffron-deep)]">written for</span> Bharat.
          </h1>
          <p className="reveal mt-6 max-w-xl text-base leading-relaxed text-stone-700 sm:text-lg" style={{ animationDelay: "0.25s" }}>
            Bharat Gatha is a journal for proud Indians: the story and roots that shaped us,
            our growth path, and a nation-first view of geopolitics.
          </p>
          <div className="reveal mt-8 flex flex-wrap gap-3" style={{ animationDelay: "0.35s" }}>
            <Link to="/articles" data-testid="hero-read-articles-btn" className="group inline-flex items-center gap-2 rounded-full bg-[var(--saffron)] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition-all hover:bg-[var(--saffron-deep)] hover:shadow-xl">
              <BookOpen className="h-4 w-4" /> Read articles
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a href="#roots" data-testid="hero-explore-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--india-green)] px-6 py-3 text-sm font-semibold text-[var(--india-green-deep)] transition-colors hover:bg-[var(--green-tint)]">
              Explore India
            </a>
          </div>
          <div className="reveal mt-10 flex items-center gap-4 text-xs text-stone-500" style={{ animationDelay: "0.45s" }}>
            <span className="h-2 w-10 rounded-full bg-[var(--saffron)]" />
            <span className="h-2 w-10 rounded-full border border-stone-300 bg-white" />
            <span className="h-2 w-10 rounded-full bg-[var(--india-green)]" />
            <span>India's Story · Growth · Geopolitics</span>
          </div>
        </div>

        <div className="relative lg:col-span-5 reveal" style={{ animationDelay: "0.3s" }}>
          <AshokaChakra className="absolute -right-10 -top-10 h-44 w-44 text-[var(--chakra)] opacity-10 chakra-spin" />
          {featured ? (
            <Link to={`/articles/${featured.slug}`} data-testid="hero-featured-card" className="card-lift group block overflow-hidden rounded-3xl border border-[var(--line)] bg-white shadow-xl shadow-orange-100/60">
              <div className="relative aspect-[4/3] overflow-hidden bg-[var(--saffron-tint)]">
                {featured.cover_image && (
                  <img src={resolveImage(featured.cover_image)} alt={featured.title} className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-[1.03]" />
                )}
                <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--chakra)] backdrop-blur">
                  Featured · {categoryLabel(featured.category)}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl font-semibold leading-snug text-[var(--chakra-deep)] group-hover:text-[var(--saffron-deep)] transition-colors">{featured.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-stone-600">{featured.excerpt}</p>
                <p className="mt-4 text-xs text-stone-500">{formatDate(featured.published_at)} · {featured.read_time} min read</p>
              </div>
            </Link>
          ) : (
            <div className="rounded-3xl border border-dashed border-[var(--line)] bg-white/70 p-10 text-center text-stone-500">
              Your first article will appear here.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
